import {
  type EnvironmentId,
  type ProjectReadFileResult,
  WS_METHODS,
} from "@afar-research/contracts";
import * as Crypto from "effect/Crypto";
import { Atom } from "effect/unstable/reactivity";

import {
  createAtomCommandScheduler,
  createEnvironmentCommand,
  createEnvironmentRpcCommand,
  createEnvironmentRpcQueryAtomFamily,
  createEnvironmentRpcSubscriptionAtomFamily,
} from "./runtime.ts";
import {
  type CreateProjectInput,
  type CreateProjectFromReferencesInput,
  type DeleteProjectInput,
  type SkipProjectStudyInput,
  type StartProjectStudyInput,
  type UpdateProjectInput,
  createProject,
  createProjectFromReferences,
  deleteProject,
  skipProjectStudy,
  startProjectStudy,
  updateProject,
} from "../operations/commands.ts";
import type { EnvironmentRegistry } from "../connection/registry.ts";

export type {
  CreateProjectInput,
  CreateProjectFromReferencesInput,
  DeleteProjectInput,
  SkipProjectStudyInput,
  StartProjectStudyInput,
  UpdateProjectInput,
} from "../operations/commands.ts";

export interface OptimisticProjectFile {
  readonly data: ProjectReadFileResult;
  readonly confirmedAgainst: object | null | undefined;
}

export interface OptimisticProjectFileTarget {
  readonly environmentId: EnvironmentId;
  readonly cwd: string;
  readonly relativePath: string;
}

export const EXPERIMENT_FILESYSTEM_QUERY_POLICY = {
  staleTimeMs: 1_000,
  idleTtlMs: 0,
} as const;

function optimisticProjectFileKey(target: OptimisticProjectFileTarget): string {
  return JSON.stringify([target.environmentId, target.cwd, target.relativePath]);
}

export function createProjectEnvironmentAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | Crypto.Crypto | R, E>,
) {
  const projectScheduler = createAtomCommandScheduler();
  const fileScheduler = createAtomCommandScheduler();
  const optimisticFileFamily = Atom.family((key: string) =>
    Atom.make<OptimisticProjectFile | null>(null).pipe(
      Atom.withLabel(`environment-data:projects:optimistic-file:${key}`),
    ),
  );
  const projectConcurrency = {
    mode: "serial" as const,
    key: ({ environmentId, input }: { environmentId: string; input: { projectId: string } }) =>
      JSON.stringify([environmentId, input.projectId]),
  };
  return {
    createAfar: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:projects:create-afar",
      tag: WS_METHODS.projectsCreateAfar,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.destinationPath]),
      },
    }),
    searchEntries: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:projects:search-entries",
      tag: WS_METHODS.projectsSearchEntries,
      staleTimeMs: 15_000,
    }),
    discoverDesignSystemMentions: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:projects:discover-design-system-mentions",
      tag: WS_METHODS.projectsDiscoverDesignSystemMentions,
      // The picker mounts this query once per open session. Immediate staleness
      // makes that mount the one fresh scan; client-side search never remounts it.
      staleTimeMs: 0,
    }),
    listEntries: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:projects:list-entries",
      tag: WS_METHODS.projectsListEntries,
      staleTimeMs: 30_000,
      idleTtlMs: 5 * 60_000,
    }),
    refreshEntries: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:projects:refresh-entries",
      tag: WS_METHODS.projectsRefreshEntries,
      scheduler: fileScheduler,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd]),
      },
    }),
    inspectAfar: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:projects:inspect-afar",
      tag: WS_METHODS.projectsInspectAfar,
      staleTimeMs: 5_000,
      refreshIntervalMs: 5_000,
      idleTtlMs: 5 * 60_000,
    }),
    inspectAfarNow: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:projects:inspect-afar-now",
      tag: WS_METHODS.projectsInspectAfar,
      scheduler: fileScheduler,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) =>
          JSON.stringify([environmentId, input.cwd, input.readOnly === true]),
      },
    }),
    assessAfar: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:projects:assess-afar",
      tag: WS_METHODS.projectsAssessAfar,
      scheduler: fileScheduler,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd]),
      },
    }),
    readinessEvents: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:projects:action-readiness-events",
      tag: WS_METHODS.subscribeProjectActionReadiness,
    }),
    readFile: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:projects:read-file",
      tag: WS_METHODS.projectsReadFile,
      staleTimeMs: 30_000,
      idleTtlMs: 5 * 60_000,
    }),
    /**
     * The Contributions tab. Read when the tab opens and cached briefly; never
     * polled, because Authorship Logs only change when a commit lands.
     */
    queryContributions: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:contributions:query",
      tag: WS_METHODS.contributionsQuery,
      staleTimeMs: 10_000,
      idleTtlMs: 5 * 60_000,
    }),
    generateProjectContribution: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:contributions:generate-project",
      tag: WS_METHODS.projectContributionGenerate,
      scheduler: fileScheduler,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) =>
          JSON.stringify([environmentId, input.projectId, input.id]),
      },
    }),
    projectContributionGenerationEvents: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:contributions:project-generation-events",
      tag: WS_METHODS.projectContributionGenerationEvents,
    }),
    cancelProjectContribution: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:contributions:cancel-project",
      tag: WS_METHODS.projectContributionCancel,
      scheduler: fileScheduler,
    }),
    exportAgentTrace: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:contributions:export-agent-trace",
      tag: WS_METHODS.contributionsExportAgentTrace,
      scheduler: fileScheduler,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd, input.range]),
      },
    }),
    listScheduleDefinitions: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:schedule:list-definitions",
      tag: WS_METHODS.scheduleListDefinitions,
      staleTimeMs: 2_000,
      refreshIntervalMs: 5_000,
      idleTtlMs: 5 * 60_000,
    }),
    listScheduleActivations: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:schedule:list-activations",
      tag: WS_METHODS.scheduleListActivations,
      staleTimeMs: 2_000,
      refreshIntervalMs: 5_000,
      idleTtlMs: 5 * 60_000,
    }),
    listScheduleOccurrences: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:schedule:list-occurrences",
      tag: WS_METHODS.scheduleListOccurrences,
      staleTimeMs: 5_000,
      refreshIntervalMs: 30_000,
      idleTtlMs: 5 * 60_000,
    }),
    listScheduleRuns: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:schedule:list-runs",
      tag: WS_METHODS.scheduleListRuns,
      staleTimeMs: 1_000,
      refreshIntervalMs: 2_000,
      idleTtlMs: 5 * 60_000,
    }),
    listDesignSystems: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:design:list-systems",
      tag: WS_METHODS.designListSystems,
      staleTimeMs: 2_000,
      refreshIntervalMs: 5_000,
      idleTtlMs: 5 * 60_000,
    }),
    /**
     * The Delete Design Operation's one send. Serial per project: two deletes
     * racing on one Design Workspace would interleave against the same
     * receipts journal, and one act at a time is how the user performs them.
     */
    deleteDesignTargets: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:design:delete-targets",
      tag: WS_METHODS.designDeleteTargets,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd]),
      },
    }),
    queryExperimentEvidence: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:experiment:query-evidence",
      tag: WS_METHODS.experimentQueryEvidence,
      ...EXPERIMENT_FILESYSTEM_QUERY_POLICY,
    }),
    queryExperimentWorkspace: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:experiment:query-workspace",
      tag: WS_METHODS.experimentQueryWorkspace,
      ...EXPERIMENT_FILESYSTEM_QUERY_POLICY,
    }),
    optimisticFile: (target: OptimisticProjectFileTarget) =>
      optimisticFileFamily(optimisticProjectFileKey(target)),
    create: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:project:create",
      surviveReadinessChanges: true,
      execute: (input: CreateProjectInput) => createProject(input),
      scheduler: projectScheduler,
      concurrency: projectConcurrency,
    }),
    createFromReferences: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:project:create-from-references",
      execute: (input: CreateProjectFromReferencesInput) => createProjectFromReferences(input),
      surviveReadinessChanges: true,
      scheduler: projectScheduler,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.commandId]),
      },
    }),
    startStudy: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:project:study-start",
      execute: (input: StartProjectStudyInput) => startProjectStudy(input),
      surviveReadinessChanges: true,
      scheduler: projectScheduler,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.commandId]),
      },
    }),
    skipStudy: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:project:study-skip",
      execute: (input: SkipProjectStudyInput) => skipProjectStudy(input),
      surviveReadinessChanges: true,
      scheduler: projectScheduler,
      concurrency: projectConcurrency,
    }),
    update: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:project:update",
      surviveReadinessChanges: true,
      execute: (input: UpdateProjectInput) => updateProject(input),
      scheduler: projectScheduler,
      concurrency: projectConcurrency,
    }),
    delete: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:project:delete",
      surviveReadinessChanges: true,
      execute: (input: DeleteProjectInput) => deleteProject(input),
      scheduler: projectScheduler,
      concurrency: projectConcurrency,
    }),
    writeFile: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:projects:write-file",
      tag: WS_METHODS.projectsWriteFile,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) =>
          JSON.stringify([environmentId, input.cwd, input.relativePath]),
      },
    }),
    saveScheduleDefinition: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:save-definition",
      tag: WS_METHODS.scheduleSaveDefinition,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) =>
          JSON.stringify([
            environmentId,
            input.cwd,
            input.existingRelativePath ?? input.definition.jobId,
          ]),
      },
    }),
    deleteScheduleDefinition: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:delete-definition",
      tag: WS_METHODS.scheduleDeleteDefinition,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) =>
          JSON.stringify([environmentId, input.cwd, input.relativePath]),
      },
    }),
    saveAndEnableSchedule: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:save-and-enable",
      tag: WS_METHODS.scheduleSaveAndEnable,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) =>
          JSON.stringify([
            environmentId,
            input.cwd,
            input.existingRelativePath ?? input.definition.jobId,
          ]),
      },
    }),
    setScheduleActivationDesiredState: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:set-activation-desired-state",
      tag: WS_METHODS.scheduleSetActivationDesiredState,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd, input.jobId]),
      },
    }),
    deleteScheduleActivation: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:delete-activation",
      tag: WS_METHODS.scheduleDeleteActivation,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd, input.jobId]),
      },
    }),
    deleteScheduleOccurrence: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:delete-occurrence",
      tag: WS_METHODS.scheduleDeleteOccurrence,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) =>
          JSON.stringify([environmentId, input.cwd, input.occurrenceId]),
      },
    }),
    runScheduleNow: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:run-now",
      tag: WS_METHODS.scheduleRunNow,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd, input.jobId]),
      },
    }),
    cancelScheduleRun: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:cancel-run",
      tag: WS_METHODS.scheduleCancelRun,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd, input.runId]),
      },
    }),
    resolveScheduleRun: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:schedule:resolve-run",
      tag: WS_METHODS.scheduleResolveRun,
      scheduler: fileScheduler,
      concurrency: {
        mode: "serial",
        key: ({ environmentId, input }) => JSON.stringify([environmentId, input.cwd, input.runId]),
      },
    }),
  };
}
